// RBAC Utility Functions
/* import { UserRole, Permission, ROLE_MODULE_ACCESS, ROLE_PERMISSIONS, MODULE_ROUTES } from '@/types/rbac'

import type { RBACUser, Module } from '@/types/rbac'
 */

// Landing-page candidates in priority order, each with the exact backend
// permission name(s) that unlock it. Mirrors the `requiredPermissions` used
// by the real sidebar (src/data/navigation/ecommerceMenuData.tsx) — the
// backend's permission names (e.g. `view-products`, `view-orders`) don't
// follow a `view-{module}` convention, so this list is kept in sync with
// that menu data rather than derived from the generic Module enum.
const DEFAULT_ROUTE_CANDIDATES: Array<{ route: string; anyPermissions: string[] }> = [
  { route: '/dashboard', anyPermissions: ['view-dashboard'] },
  { route: '/orders', anyPermissions: ['view-orders'] },
  { route: '/catalog/products', anyPermissions: ['view-products', 'view-categories', 'view-brands'] },
  { route: '/marketing/slides', anyPermissions: ['view-slides', 'view-push-campaigns', 'view-reels'] },
  { route: '/tickets', anyPermissions: ['view-tickets'] },
  { route: '/customers', anyPermissions: ['view-users'] },
  { route: '/shipments', anyPermissions: ['view-orders'] },
  { route: '/payments/transactions', anyPermissions: ['view-orders'] },
  { route: '/reports', anyPermissions: ['view-reports'] },
  { route: '/users', anyPermissions: ['view-users', 'add-user'] },
  { route: '/users/roles', anyPermissions: ['view-roles'] },
  { route: '/system-alerts', anyPermissions: ['view-system-alerts'] },
  { route: '/cms/terms', anyPermissions: ['view-settings'] },
  { route: '/otp/sms', anyPermissions: ['view-settings'] },
  { route: '/settings', anyPermissions: ['view-settings'] }
]

/**
 * Pick a route the user is actually allowed to land on, based on their live
 * permissions — instead of assuming everyone can see `/en/dashboard`.
 * Falls back to the 401 page if nothing is accessible (should not normally
 * happen for an active account, but avoids a redirect loop either way).
 */
export function getDefaultRoute(permissions: string[], userRole?: string | null): string {
  if (userRole?.toUpperCase() === 'ROOT') {
    return `/en${DEFAULT_ROUTE_CANDIDATES[0].route}`
  }

  const candidate = DEFAULT_ROUTE_CANDIDATES.find(c => c.anyPermissions.some(p => permissions.includes(p)))

  return candidate ? `/en${candidate.route}` : '/en/pages/misc/401-not-authorized'
}

/**
 * Check if a user has access to a specific module
 */
/* export function hasModuleAccess(userRole: UserRole, module: Module): boolean {
  const allowedModules = ROLE_MODULE_ACCESS[userRole]

  return allowedModules.includes(module)
}
 */
/**
 * Check if a user has a specific permission for a module
 */
/* export function hasPermission(userRole: UserRole, module: Module, permission: Permission): boolean {
  if (!hasModuleAccess(userRole, module)) {
    return false
  }

  const permissions = ROLE_PERMISSIONS[userRole][module]

  return permissions.includes(permission)
} */

/**
 * Get all accessible modules for a user role
 */
/* export function getAccessibleModules(userRole: UserRole): Module[] {
  return ROLE_MODULE_ACCESS[userRole]
}
 */
/**
 * Get all permissions for a specific module based on user role
 */
/* export function getModulePermissions(userRole: UserRole, module: Module): Permission[] {
  if (!hasModuleAccess(userRole, module)) {
    return []
  }

  return ROLE_PERMISSIONS[userRole][module]
} */

/**
 * Check if a route is accessible by a user role
 */
/* export function canAccessRoute(userRole: UserRole, pathname: string): boolean {
  // Extract the base path without dynamic segments
  const basePath = pathname.split('/').slice(0, 3).join('/')

  // Check each module's routes
  for (const [module, routes] of Object.entries(MODULE_ROUTES)) {
    for (const route of routes) {
      // Handle dynamic routes like /orders/[id]
      const routePattern = route.replace(/\[.*?\]/g, '[^/]+')
      const regex = new RegExp(`^${routePattern}$`)

      if (regex.test(pathname) || pathname.startsWith(route.replace(/\/\[.*?\]/, ''))) {
        return hasModuleAccess(userRole, module as Module)
      }
    }
  }

  return false
} */

/**
 * Filter navigation items based on user role
 */
/* export function filterNavigationByRole<T extends { href?: string; children?: T[] }>(
  items: T[],
  userRole: UserRole
): T[] {
  return items
    .map(item => {
      // If item has children, filter them recursively
      if (item.children) {
        const filteredChildren = filterNavigationByRole(item.children, userRole)

        // If no children remain after filtering, exclude the parent
        if (filteredChildren.length === 0) {
          return null
        }

        return { ...item, children: filteredChildren }
      }

      // If item has href, check if user can access it
      if (item.href) {
        const canAccess = canAccessRoute(userRole, item.href)

        if (!canAccess) {
          return null
        }
      }

      return item
    })
    .filter((item): item is T => item !== null)
} */

/**
 * Check if user can manage another user based on their roles
 * Admin can manage all
 * Supervisor can manage all Support roles but not Admin or other Supervisors
 * Support roles cannot manage anyone
 */
/* export function canManageUser(managerRole: UserRole, targetRole: UserRole): boolean {
  if (managerRole === UserRole.ADMIN) {
    return true
  }

  if (managerRole === UserRole.SUPERVISOR) {
    return (
      targetRole === UserRole.MARKETING_SUPPORT ||
      targetRole === UserRole.SHOP_SUPPORT ||
      targetRole === UserRole.ORDER_SUPPORT ||
      targetRole === UserRole.CUSTOMER_SUPPORT
    )
  }

  return false
} */

/**
 * Get user role display name
 */
/* export function getRoleDisplayName(role: UserRole): string {
  const roleNames: Record<UserRole, string> = {
    [UserRole.ADMIN]: 'Admin',
    [UserRole.SUPERVISOR]: 'Supervisor',
    [UserRole.MARKETING_SUPPORT]: 'Marketing Support',
    [UserRole.SHOP_SUPPORT]: 'Shop Support',
    [UserRole.ORDER_SUPPORT]: 'Order Support',
    [UserRole.CUSTOMER_SUPPORT]: 'Customer Support'
  }

  return roleNames[role]
} */

/**
 * Check if user is blocked or inactive
 */
/* export function isUserAllowed(user: RBACUser): boolean {
  return user.isActive && !user.isBlocked
}
 */
/**
 * Get restricted actions for a role in a specific module
 */
/* export function getRestrictedActions(userRole: UserRole, module: Module): Permission[] {
  const allPermissions = Object.values(Permission)
  const allowedPermissions = getModulePermissions(userRole, module)

  return allPermissions.filter(permission => !allowedPermissions.includes(permission))
}
 */
